import { FC, useState, ReactNode } from "react";
import { Transition } from "@headlessui/react";
import { X, Check } from "lucide-react";
import { InfoIcon, XMarkIcon } from "components/icons/Icons";

type MessageType = "success" | "error" | "warning" | "info";

type AnchorPosition =
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center"
  | "left-center"
  | "right-center";

export interface MessageSection {
  /** Titre de la section */
  title?: string;
  /** Liste des messages de cette section */
  messages: string[];
  /** Type de message qui détermine l'icône et le style */
  type: MessageType;
}

interface MessagesTooltipProps {
  /** Contenu qui déclenche l'affichage du tooltip au survol */
  children: ReactNode;
  /** Liste des sections à afficher */
  sections: MessageSection[];
  /** Position d'ancrage du tooltip (par défaut: "bottom-left") */
  anchor?: AnchorPosition;
}

const getAnchorClasses = (anchor: AnchorPosition): string => {
  const positions: Record<AnchorPosition, string> = {
    "top-left": "bottom-full left-0 mb-1",
    "top-right": "bottom-full right-0 mb-1",
    "top-center": "bottom-full left-1/2 -translate-x-1/2 mb-1",
    "bottom-left": "top-full left-0 mt-1",
    "bottom-right": "top-full right-0 mt-1",
    "bottom-center": "top-full left-1/2 -translate-x-1/2 mt-1",
    "left-center": "right-full top-1/2 -translate-y-1/2 mr-1",
    "right-center": "left-full top-1/2 -translate-y-1/2 ml-1",
  };

  return positions[anchor];
};

const getIconForType = (type: MessageType) => {
  switch (type) {
    case "success":
      return (
        <div className="flex-none mt-0.5 w-3 h-3 rounded-full border border-green-600 flex items-center justify-center">
          <Check className="text-green-600 w-2 h-2" />
        </div>
      );
    case "error":
      return (
        <div className="flex-none w-3 h-3 mt-0.5 rounded-full border border-secondary flex items-center justify-center">
          <X className="text-secondary w-2 h-2" />
        </div>
      );
    case "warning":
      return (
        <div className="flex-none w-3 h-3 mt-0.5 rounded-full border border-secondary">
          <XMarkIcon className="text-secondary w-full h-full" />
        </div>
      );
    case "info":
      return <InfoIcon className="text-primary w-4 h-4" />;
  }
};

export const MessagesTooltip: FC<MessagesTooltipProps> = ({
  children,
  sections,
  anchor = "bottom-left",
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  // Filtrer les sections qui ont des messages
  const sectionsWithMessages = sections.filter(
    (section) => section.messages.length > 0
  );

  // Ne rien afficher si aucune section n'a de messages
  if (sectionsWithMessages.length === 0) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setIsTooltipOpen(true)}
      onMouseLeave={() => setIsTooltipOpen(false)}
    >
      {children}
      <Transition show={isTooltipOpen}>
        <div
          className={`absolute text-xs z-50 w-72 sm:w-80 md:w-96 bg-white border border-gray-200 rounded-lg shadow-lg p-3 transition duration-300 ease-in data-closed:opacity-0 data-closed:scale-95 ${getAnchorClasses(anchor)}`}
        >
          {sectionsWithMessages.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              className={sectionIndex > 0 ? "mt-3" : ""}
            >
              {section.title && (
                <p className="font-bold mb-2 break-words">{section.title}</p>
              )}
              {section.messages.map((message, messageIndex) => (
                <div
                  key={messageIndex}
                  className="flex items-start gap-2 mb-1 last:mb-0"
                >
                  {getIconForType(section.type)}
                  <p className="break-words overflow-wrap-anywhere flex-1">{message}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </Transition>
    </div>
  );
};
